//Avatar component
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GAME_CONFIG } from '../../utils/gameConfig';
import { Animal } from '../../types';
import { getColorValue } from '../../utils/colors';


interface AvatarProps {
  x: number;
  y: number;
  direction: 'up' | 'down' | 'left' | 'right';
  animal: Animal;
  isMoving: boolean;
  preferredColor?: string;
  className?: string;
}

const Avatar: React.FC<AvatarProps> = ({ 
  x, 
  y, 
  direction,
  animal, 
  isMoving,
  preferredColor,
  className = '' 
}) => {
  
  const [imageError, setImageError] = useState(false);
  
  console.log(`Avatar props:`, { x, y, direction, animalName: animal.name, preferredColor });
  
  //Sprite path
  const getSpritePath = () => {
    return animal.avatarPaths[direction];
  };

  //Pixel position
  const pixelX = x * GAME_CONFIG.TILE_SIZE;
  const pixelY = y * GAME_CONFIG.TILE_SIZE;

  //Color or default
  const userColor = getColorValue(preferredColor);

  //Fallback (arrows for now)
  const getDirectionArrow = () => {
    switch (direction) {
      case 'up': return '↑';
      case 'down': return '↓';
      case 'left': return '←';
      case 'right': return '→';
      default: return '●';
    }
  };

  return (
    <motion.div
      className={`absolute z-20 ${className}`}
      style={{
        left: pixelX,
        top: pixelY,
        width: GAME_CONFIG.TILE_SIZE,
        height: GAME_CONFIG.TILE_SIZE,
      }}
      animate={{
        x: 0,
        y: 0,
      }}
      transition={{
        duration: 0.2,
        ease: "easeOut"
      }}
    >
      {/* Avatar container */}
      <div 
        className="relative w-full h-full"
        style={{
          borderRadius: '50%',
          border: userColor ? `3px solid ${userColor}` : 'none',
          boxShadow: userColor ? `0 0 8px ${userColor}40` : 'none',
        }}
      >
        {!imageError ? (
          <img
            src={getSpritePath()}
            alt={`${animal.name} facing ${direction}`}
            className="w-full h-full object-contain"
            style={{
              imageRendering: 'pixelated',
              borderRadius: '50%',
            }}
            onError={() => {
              console.log(`Image failed to load: ${getSpritePath()}`);
              setImageError(true);
            }}
            onLoad={() => {
              console.log(`Image loaded successfully: ${getSpritePath()}`);
            }}
          />
        ) : (
          <div 
            className="w-full h-full flex items-center justify-center border-4"
            style={{ 
              backgroundColor: '#ef4444',
              borderColor: userColor || '#fbbf24',
              borderRadius: '50%',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '32px',
              textShadow: '2px 2px 4px black'
            }}
          >
            {getDirectionArrow()}
            <div className="absolute bottom-0 left-0 text-xs bg-black/80 text-white px-1 rounded">
              Missing: {animal.name}-{direction}
            </div>
          </div>
        )}

        {/* Color overlay */}
        {userColor && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{ 
              backgroundColor: userColor,
              opacity: 0.1,
              borderRadius: '50%'
            }}
          />
        )}
      </div>

      {/* Movement */}
      {isMoving && (
        <motion.div
          className="absolute inset-0"
          style={{ 
            backgroundColor: userColor ? `${userColor}33` : '#ffffff33',
            borderRadius: '50%'
          }}
          initial={{ scale: 0.8, opacity: 0.8 }}
          animate={{ scale: 1.2, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      )}
    </motion.div>
  );
};

export default Avatar;